import { FC, useCallback, useMemo } from "react";
import { Controller, FieldError, useFormContext } from "react-hook-form";
import { isArray, isEmpty } from "lodash";
import { FormHelperText } from "@mui/material";
import {
  DateRangeCalendar,
  DateRangePickerDayProps,
} from "@mui/x-date-pickers-pro";
import __ from "src/utils/Translations";
import { parseISO, getYear, getMonth } from "date-fns";
import { transformAbsencePlanToPlannedDays } from "src/utils/Company/Absences/transformAbsencePlan";
import { Loader } from "src/Components/Loader/Loader";
import CustomDateRangePickersDay from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker/CustomDateRangePickersDay";
import {
  IAbsencePlan,
  IAbsenceRequestDay,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import { datepickerStyles } from "./Datepicker.styles";

interface IAbsenceRequestDatepickerProps {
  absenceRequestDays: IAbsenceRequestDay[];
  excludedDays: IExcludedDay[];
  absencePlanData?: IAbsencePlan;
  multipleRequestsPerDayAllowed: boolean;
  billInCalendarDays: boolean;
  loading: boolean;
  disableMonthChange?: boolean;
  handleMonthChange: (month: Date) => void;
  yearsWithCalendar: string[];
  displayMonths: { from: string; to: string };
}

const AbsenceRequestDatepicker: FC<IAbsenceRequestDatepickerProps> = ({
  absenceRequestDays,
  excludedDays,
  absencePlanData,
  multipleRequestsPerDayAllowed,
  billInCalendarDays,
  loading,
  disableMonthChange = false,
  handleMonthChange,
  yearsWithCalendar,
  displayMonths,
}) => {
  const { control } = useFormContext();

  const currentMonthDate = parseISO(displayMonths.from);
  const currentYear = getYear(currentMonthDate);
  const yearsAsNumbers = yearsWithCalendar.map(Number);
  const minYear = !isEmpty(yearsAsNumbers) ? Math.min(...yearsAsNumbers) : null;
  const maxYear = !isEmpty(yearsAsNumbers) ? Math.max(...yearsAsNumbers) : null;
  // Hide prev arrow if employee doesn't have an assigned calendar for previous year relative to current datepicker position
  const hidePrev =
    minYear !== null &&
    currentYear === minYear &&
    getMonth(currentMonthDate) === 0;
  // Hide next arrow if employee doesn't have an assigned calendar for next year relative to current datepicker position
  const hideNext =
    maxYear !== null &&
    currentYear === maxYear &&
    getMonth(currentMonthDate) === 11;

  const plannedDays = useMemo(() => {
    if (!absencePlanData) return [];

    return transformAbsencePlanToPlannedDays(absencePlanData);
  }, [absencePlanData]);

  const dayRenderer = useCallback(
    (props: DateRangePickerDayProps) => (
      <CustomDateRangePickersDay
        absenceRequestDays={absenceRequestDays}
        excludedDays={excludedDays}
        plannedDays={plannedDays}
        multipleRequestsPerDayAllowed={multipleRequestsPerDayAllowed}
        billInCalendarDays={billInCalendarDays}
        {...props}
      />
    ),
    [
      absenceRequestDays,
      excludedDays,
      plannedDays,
      multipleRequestsPerDayAllowed,
      billInCalendarDays,
    ]
  );

  return (
    <Controller
      control={control}
      name="dates"
      render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
        <>
          <DateRangeCalendar
            ref={ref}
            calendars={2}
            value={value}
            loading={loading}
            disableAutoMonthSwitching
            disableHighlightToday
            autoFocus={false}
            onChange={onChange}
            onMonthChange={handleMonthChange}
            dayOfWeekFormatter={(date: Date) =>
              date.toLocaleString("pl", { weekday: "short" })
            }
            renderLoading={() => <Loader />}
            slots={{
              day: dayRenderer,
            }}
            slotProps={{
              previousIconButton: {
                sx: hidePrev ? { visibility: "hidden" } : undefined,
              },
              nextIconButton: {
                sx: hideNext ? { visibility: "hidden" } : undefined,
              },
            }}
            sx={{
              ...datepickerStyles,
              ...(disableMonthChange && {
                ".MuiPickersArrowSwitcher-button": {
                  visibility: "hidden",
                },
              }),
            }}
            data-testid="absence-request-form-field__date-range"
          />
          <FormHelperText error={!!error}>
            {isArray(error)
              ? (error as unknown as FieldError[])?.map((item) => (
                  <>{__(item.message!)}</>
                ))
              : error?.message && __(error.message)}
          </FormHelperText>
        </>
      )}
    />
  );
};

export default AbsenceRequestDatepicker;
